import { Badge, Button, Card, IconButton, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Globe, Plus, RotateCw } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { FlowResponse } from "@/api";
import {
  createFlowTriggerMutation,
  errorMessage,
  getFlowQueryKey,
  listFlowTriggersOptions,
  listFlowTriggersQueryKey,
  regenerateWebhookSecretMutation,
} from "@/api";
import { Text } from "@/components/Text";

/*
 * The one-time webhook secret, from the two responses that carry it. Neither
 * is typed in the OpenAPI schema (the create endpoint is an untyped 201), so
 * these read the real wire shapes:
 *  - POST /flows/{uuid}/triggers → {"data": {...trigger, "webhook_secret": "…"}}
 *    (the backend adds the snake_case key after serializing the camelCase model)
 *  - POST /webhooks/{uuid}/regenerate-secret → {"data": {"secret": "…"}}
 */
export function createdWebhookSecret(res: unknown): string | undefined {
  return (res as { data?: { webhook_secret?: string } } | undefined)?.data?.webhook_secret;
}
export function regeneratedSecret(res: unknown): string | undefined {
  return (res as { data?: { secret?: string } } | undefined)?.data?.secret;
}

/**
 * The inbound-webhook section for a workflow (ported from the old UI's
 * FlowWebhookSection). A webhook is a trigger of type "webhook"; creating one
 * returns a secret exactly once. Shows the endpoint URL + a copy button, and
 * lets you regenerate the secret (which invalidates the old one immediately).
 */
export function FlowWebhookSection({ flow }: Readonly<{ flow: FlowResponse }>) {
  const { t } = useTranslation("workflows");
  const toast = useToast();
  const queryClient = useQueryClient();
  const [secret, setSecret] = useState<string | null>(null);

  // The webhook comes from the triggers list (the flow's own `triggers` are
  // summaries without it); the flow is refreshed too, as it lists triggers.
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getFlowQueryKey({ path: { flow_uuid: flow.uuid } }) }),
      queryClient.invalidateQueries({ queryKey: listFlowTriggersQueryKey({ path: { flow_uuid: flow.uuid } }) }),
    ]);

  // The webhook trigger, if one exists (a webhook carries a registration).
  const list = useQuery(listFlowTriggersOptions({ path: { flow_uuid: flow.uuid } }));
  const webhookTrigger = list.data?.data.find((tr) => tr.triggerType === "webhook" && tr.webhook);
  const webhook = webhookTrigger?.webhook;
  const url = webhook?.webhookUrl ?? (webhook ? `${globalThis.location.origin}/api/v1/hooks/${webhook.slug}` : null);

  const enable = useMutation({
    ...createFlowTriggerMutation(),
    onSuccess: async (res) => {
      await invalidate();
      const created = createdWebhookSecret(res);
      if (created) setSecret(created);
      toast.success(t("webhook.enabled"));
    },
    onError: (e) => toast.error(errorMessage(e, t("webhook.enableFailed"))),
  });

  const regenerate = useMutation({
    ...regenerateWebhookSecretMutation(),
    onSuccess: (res) => {
      const fresh = regeneratedSecret(res);
      if (fresh) setSecret(fresh);
      toast.success(t("webhook.regenerated"));
    },
    onError: (e) => toast.error(errorMessage(e, t("webhook.regenerateFailed"))),
  });

  const copy = (text: string, label: string) => {
    void navigator.clipboard.writeText(text);
    toast.success(t("webhook.copied", { label }));
  };

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Globe size={16} aria-hidden className="text-content-secondary" />
          <Text variant="body">{t("webhook.title")}</Text>
        </div>

        {webhook && url ? (
          <>
            <div className="flex flex-col gap-1">
              <Text variant="caption" tone="tertiary">
                {t("webhook.endpoint")}
              </Text>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-lg border border-border-input bg-surface-secondary px-3 py-2 [font:var(--type-code)] text-content-primary">
                  {url}
                </code>
                <IconButton
                  icon={<Copy />}
                  aria-label={t("webhook.copyUrl")}
                  variant="ghost"
                  size="sm"
                  onClick={() => copy(url, t("webhook.endpoint"))}
                />
              </div>
            </div>

            {secret ? (
              <div className="flex flex-col gap-1">
                <Text variant="caption" tone="tertiary">
                  {t("webhook.secret")}
                </Text>
                <div className="flex items-center gap-2">
                  <code className="min-w-0 flex-1 truncate rounded-lg border border-border-input bg-surface-secondary px-3 py-2 [font:var(--type-code)] text-success-on-subtle">
                    {secret}
                  </code>
                  <IconButton
                    icon={<Copy />}
                    aria-label={t("webhook.copySecret")}
                    variant="ghost"
                    size="sm"
                    onClick={() => copy(secret, t("webhook.secret"))}
                  />
                </div>
                <Text variant="caption" tone="warning">
                  {t("webhook.secretOnce")}
                </Text>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {!webhook.isActive && <Badge variant="neutral">{t("status.pending")}</Badge>}
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={<RotateCw size={14} />}
                  loading={regenerate.isPending}
                  onClick={() => webhookTrigger && regenerate.mutate({ path: { webhook_uuid: webhook.uuid } })}
                >
                  {t("webhook.regenerate")}
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <Text variant="body-sm" tone="tertiary">
              {t("webhook.empty")}
            </Text>
            <div>
              <Button
                variant="primary"
                size="sm"
                leadingIcon={<Plus size={14} />}
                loading={enable.isPending}
                disabled={!list.isSuccess}
                onClick={() =>
                  enable.mutate({
                    path: { flow_uuid: flow.uuid },
                    body: { triggerType: "webhook", name: t("webhook.defaultName") },
                  })
                }
              >
                {t("webhook.enable")}
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
